import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import DetailViewLayout from "../../layouts/DetailViewLayout";
import useProgram from "../../utils/hooks/programs/useProgram";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import { UserRole } from "../../utils/api/endpoints/user/credentials";
import { Tab } from "../../layouts/TabbedLayout";
import { HiInformationCircle, HiExclamation, HiCheckCircle, HiShieldCheck, HiMail } from "react-icons/hi";
import FullProgramDetails from "../../components/programs/FullProgramDetails";
import ProgramStats from "../../components/programs/ProgramStats";
import SendSummaryEmailModal from "../../components/programs/SendSummaryEmailModal";
import RightbarProgram from "../../components/programs/cards/RightbarProgram";
import Sidebar from "../../components/programs/cards/HamburgerProgram";
import ErrorMessage from "../../components/forms/errors/ErrorMessage";
import ProgramHighlight from "../../components/programs/cards/ProgramHighlight";
import ReportTable from "./section/ReportTable";
import Management from "./section/Management";
import TermsConditions from "./section/TermsCondition";
import ScopeTable from "./section/Scope";
import RecentActivity from "./section/RecentActivity";
import Attachments from "./section/Attachments";
import DiscoveredEndpointsTable from "./section/DiscoveredEndpointsTable";
import ScheduledRuns from "./section/ScheduledRuns";
import Documentation from "./section/Documentation";
import Footer from "../../components/footer/Footer";

import ProgramChecklistsView from "../../components/checklists/ProgramChecklistsView";
import { createAutomatedReport, checkProgramAutomatedReport } from "../../utils/api/endpoints/automated-reports/automated-reports";
import toast from "react-hot-toast";
import PentestReportConfirmationModal from '../../components/common/PentestReportConfirmationModal';
import PhaseTimeline from '../../components/programs/PhaseTimeline';
import PhaseStatusPill, { PhaseStatus, toPhaseStatus } from '../../components/programs/PhaseStatusPill';
import MarkReadyButton from '../../components/programs/MarkReadyButton';
import axios from "../../utils/api/axios";
import reportBugIcon from "../../assets/icons/report-a-bug.svg";
import requestPentestIcon from "../../assets/icons/request-pentest.svg";
import { FiMessageSquare, FiX, FiStar, FiDownload, FiFileText } from "react-icons/fi";
import { useGetFeedbackStatusQuery, useGetProgramsNeedingFeedbackQuery } from "../../utils/api/endpoints/program-feedback/programFeedbackApi";
import { useRequestFeedbackMutation, useUndoRequestFeedbackMutation } from "../../utils/api/endpoints/admin/adminFeedbackApi";
import { CredentialVault } from "../../components/vault";
import { HiLockClosed } from "react-icons/hi";

import useProgramRuns from "../../utils/hooks/programs/useProgramRuns";
import { isProgramDraft, isScopeSnapshotPending, isScopePendingVerification } from "../../utils/programs/statusUtils";
import { motion, AnimatePresence } from "framer-motion";

const sections: { name: string; content: string }[] = [
  { name: "Overview", content: "This is the Overview content." },
  { name: "Scope", content: "Assets and targets in scope for this program." },
  { name: "Scheduled Runs", content: "View all scheduled testing runs for this program." },
  { name: "Discovery", content: "View and add discovered endpoints for this program." },
  { name: "Attachments", content: "List of program's attachments." },
  { name: "Test Checklists", content: "Test coverage and security checklists." },
  { name: "Recent Activity", content: "View recent activity logs here." },
  { name: "Reports", content: "Access reports and analytics." },
  { name: "Management", content: "Manage program settings and configurations." },
  { name: "Terms & Conditions", content: "Read the program terms here." },
];

const documentationSection = { name: "Documentation", content: "View asset architecture, runbooks, and other documentation." };
const credentialVaultSection = { name: "Credential Vault", content: "Access shared credentials and secure notes for this program." };

const isBeforeDate = (dateString: string) => {
  if (!dateString) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const date = new Date(dateString);
  date.setHours(0, 0, 0, 0);
  return today < date;
};

const ProgramPage = () => {
  const { id } = useParams();
  const { role } = useUserCredentials();
  const isAuthorizedForReport = role && [
    UserRole.BUSINESS,
    UserRole.ADMIN,
    UserRole.SUB_ADMIN,
    UserRole.QA
  ].includes(role as UserRole);
  const navigate = useNavigate();
  const [showStats, setShowStats] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [activeSection, setActiveSection] = useState("Overview");
  const [isRequestingReport, setIsRequestingReport] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [hasPendingReport, setHasPendingReport] = useState(false);
  const [hasApprovedReport, setHasApprovedReport] = useState(false);
  const [approvedReportId, setApprovedReportId] = useState<string | null>(null);
  const [approvedReports, setApprovedReports] = useState<any[]>([]);
  const [latestReport, setLatestReport] = useState<any>(null);
  const [nextVersion, setNextVersion] = useState(1);
  const [canRequestNewVersion, setCanRequestNewVersion] = useState(true);
  const [restrictionReason, setRestrictionReason] = useState<string | null>(null);
  const [isCheckingReport, setIsCheckingReport] = useState(true);
  const [programOwnerCompanyId, setProgramOwnerCompanyId] = useState<number | null>(null);
  const [isHamburgerOpen, setIsHamburgerOpen] = useState(false);
  const [triggerAutoOpen, setTriggerAutoOpen] = useState<number>(0);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [feedbackSelectedRunId, setFeedbackSelectedRunId] = useState<number | 'overall'>('overall');
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [selectedRunForSummary, setSelectedRunForSummary] = useState<any>(null);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const urlRunId = queryParams.get("runId");
  const urlSection = queryParams.get("section");
  const [selectedRunId, setSelectedRunId] = useState<number | undefined>(
    urlRunId ? parseInt(urlRunId, 10) : undefined
  );

  const { program, isLoading } = useProgram(id ? parseInt(id, 10) : undefined);
  const { runs, refetch: refetchRuns } = useProgramRuns(id ? parseInt(id, 10) : undefined);

  // Get feedback status for business users and admins
  const { data: feedbackStatus, refetch: refetchFeedbackStatus } = useGetFeedbackStatusQuery(
    { programId: id ? parseInt(id, 10) : 0, runId: undefined },
    {
      skip: !id || (![UserRole.BUSINESS, UserRole.BUSINESS_ADMINISTRATOR, UserRole.ADMIN, UserRole.SUB_ADMIN].includes(role as UserRole))
    }
  );

  const { data: allPendingFeedbacks } = useGetProgramsNeedingFeedbackQuery(undefined, {
    skip: !id || (role !== UserRole.BUSINESS && role !== UserRole.BUSINESS_ADMINISTRATOR)
  });

  const pendingFeedbacksForThisProgram = Array.isArray(allPendingFeedbacks)
    ? allPendingFeedbacks.filter(f => f.program_id === parseInt(id!, 10))
    : [];

  const [requestFeedbackMutation, { isLoading: isRequestingFeedback }] = useRequestFeedbackMutation();
  const [undoRequestFeedbackMutation, { isLoading: isUndoingFeedback }] = useUndoRequestFeedbackMutation();
  const [isUndoConfirmModalOpen, setIsUndoConfirmModalOpen] = useState(false);

  const executeAdminRequestFeedback = async (runIdToRequest: number | undefined) => {
    if (!program || !program.userId) return;
    try {
      await requestFeedbackMutation({
        program_id: program.id,
        run_id: runIdToRequest,
        business_user_id: program.userId
      }).unwrap();
      toast.success("Feedback requested successfully!");
      setIsFeedbackModalOpen(false);
      refetchFeedbackStatus();
      refetchRuns();
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to request feedback");
    }
  };

  const executeAdminUndoFeedback = async () => {
    if (!program || !program.userId) return;
    try {
      await undoRequestFeedbackMutation({
        program_id: program.id,
        run_id: selectedRunId,
        business_user_id: program.userId
      }).unwrap();
      toast.success("Feedback request taken back successfully!");
      setIsUndoConfirmModalOpen(false);
      refetchFeedbackStatus();
      refetchRuns();
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to take back feedback request");
    }
  };

  const handleAdminRequestFeedbackClick = () => {
    if (feedbackStatus?.is_requested) {
      setIsUndoConfirmModalOpen(true);
    } else {
      setIsFeedbackModalOpen(true);
      if (program?.scheduling_type === 'continuous') {
        setFeedbackSelectedRunId('overall');
      } else {
        setFeedbackSelectedRunId('overall'); // Use overall for one-off but execute right away or via confirmation? We will just show the same modal as confirmation
      }
    }
  };

  const isProgramEnded = program?.expected_end_date
    ? new Date(program.expected_end_date) <= new Date()
    : false;

  const checkAutomatedReports = useCallback(async () => {
    if (!id) return;
    setIsCheckingReport(true);
    try {
      let res;
      const isPentestListAdmin = role && [UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.QA].includes(role as UserRole);
      if (isPentestListAdmin) {
        const response = await axios.get(`/v2/automated-report/admin/pentest-reports?limit=100&program_id=${id}`);
        res = response.data;
      } else {
        try {
          const response = await axios.get('/v2/automated-report/business-reports');
          res = response.data;
        } catch (err) {
          console.warn("Could not fetch business reports directly:", err);
          res = { status: 'success', data: { pending_reports: [] } };
        }
      }
      if (res && res.status === 'success') {
        const rawData = res.data || {};
        const reportsList = [
          ...(rawData.reports || []),
          ...(rawData.pending_reports || []),
          ...(rawData.approved_reports || []),
          ...(res.pending_reports || []),
          ...(res.approved_reports || []),
          ...(res.reports || [])
        ];

        // Filter reports containing this program_id
        const programReports = reportsList.filter((item: any) => {
          const reportObj = item.report || item;
          // Support parsed arrays, serialized JSON strings, single program_id, and keys of program_titles map
          let progIds: number[] = [];
          if (reportObj.program_ids) {
            try {
              progIds = Array.isArray(reportObj.program_ids)
                ? reportObj.program_ids
                : JSON.parse(reportObj.program_ids || "[]");
            } catch (e) {
              progIds = [];
            }
          } else if (reportObj.program_id || reportObj.programId) {
            progIds = [Number(reportObj.program_id || reportObj.programId)];
          }

          if (reportObj.program_titles && typeof reportObj.program_titles === "object") {
            Object.keys(reportObj.program_titles).forEach(k => {
              const numKey = Number(k);
              if (!isNaN(numKey)) {
                progIds.push(numKey);
              }
            });
          }
          return progIds.map(Number).includes(Number(id));
        });

        // Find approved reports
        const approvedList = programReports.filter(item => {
          const reportObj = item.report || item;
          const reportStatus = item.report ? reportObj.status : (item.status || reportObj.status);
          return ['approved', 'business_approved', 'business_review', 'business_requested_changes'].includes(reportStatus);
        });

        // Find pending report
        const pendingReport = programReports.find(item => {
          const reportObj = item.report || item;
          const reportStatus = item.report ? reportObj.status : (item.status || reportObj.status);
          return ['pending', 'draft', 'qa_review', 'admin_review', 'rejected', 'changes_added', 'report_updated'].includes(reportStatus);
        });

        // Get the latest report to determine status and version restriction
        let latestReportObj = null;
        if (programReports.length > 0) {
          const sorted = [...programReports].sort((a, b) => {
            const aObj = a.report || a;
            const bObj = b.report || b;
            return bObj.id - aObj.id;
          });
          latestReportObj = sorted[0].report || sorted[0];
        }

        setApprovedReports(approvedList);
        setLatestReport(latestReportObj);
        setHasPendingReport(!!pendingReport);
        setHasApprovedReport(approvedList.length > 0);
        setApprovedReportId(approvedList.length > 0 ? (approvedList[0].report?.id || approvedList[0].id) : null);
      }
    } catch (error) {
      console.error("Error checking automated reports:", error);
    } finally {
      setIsCheckingReport(false);
    }
  }, [id, role]);

  const fetchProgramOwnerCompany = useCallback(async () => {
    if (!program?.userId || !role) return;
    const isAdmin = [UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.ADMIN_MANAGER].includes(role);
    if (!isAdmin) return;
    try {
      const response = await axios.get(`/v2/user?id=${program.userId}`);
      if (response.data?.user?.company_id) {
        setProgramOwnerCompanyId(response.data.user.company_id);
      }
    } catch (error) {
      console.error('Error fetching program owner company:', error);
    }
  }, [program?.userId, role]);

  useEffect(() => {
    if (id && isAuthorizedForReport) {
      checkAutomatedReports();
    }
    const handleReportRequested = () => {
      checkAutomatedReports();
      if (typeof (window as any).refreshLatestRunReport === 'function') {
        (window as any).refreshLatestRunReport();
      }
    };
    window.addEventListener("report-requested", handleReportRequested);
    return () => {
      window.removeEventListener("report-requested", handleReportRequested);
    };
  }, [id, role, checkAutomatedReports]);

  useEffect(() => {
    fetchProgramOwnerCompany();
  }, [fetchProgramOwnerCompany]);

  useEffect(() => {
    if (!id) return;
    const hasActiveOrPausedRun = runs?.some(r => r.status === 'active' || r.status === 'paused');
    if (hasActiveOrPausedRun) {
      const interval = setInterval(() => {
        refetchRuns();
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [id, runs, refetchRuns]);

  const [latestRunHasReport, setLatestRunHasReport] = useState(false);
  const [latestRunReportDownloadId, setLatestRunReportDownloadId] = useState<string | null>(null);
  const [downloadReportCount, setDownloadReportCount] = useState(0);
  const [singleDownloadRunTitle, setSingleDownloadRunTitle] = useState("");
  const [singleDownloadRunId, setSingleDownloadRunId] = useState<number | null>(null);
  const [targetRunForReport, setTargetRunForReport] = useState<any>(null);

  useEffect(() => {
    const checkLatestRunReport = async () => {
      const completedRuns = (runs || []).filter(r => r.status === 'completed');
      if (!id || completedRuns.length === 0 || program?.scheduling_type !== 'continuous') return;
      try {
        let res;
        const isPentestListAdmin = role && [UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.QA].includes(role as UserRole);
        if (isPentestListAdmin) {
          const response = await axios.get(`/v2/automated-report/admin/pentest-reports?limit=100&program_id=${id}`);
          res = response.data;
        } else {
          try {
            const response = await axios.get('/v2/automated-report/business-reports');
            res = response.data;
          } catch (err) {
            console.warn("Could not fetch business reports directly:", err);
            res = { status: 'success', data: { pending_reports: [] } };
          }
        }
        if (res && res.status === 'success') {
          const rawData = res.data || {};
          const reportsList = [
            ...(rawData.reports || []),
            ...(rawData.pending_reports || []),
            ...(rawData.approved_reports || []),
            ...(res.pending_reports || []),
            ...(res.approved_reports || []),
            ...(res.reports || [])
          ];

          // Find the latest completed run that does NOT have a report
          const sorted = [...completedRuns].sort((a, b) => b.run_id - a.run_id);
          let targetRun = null;
          let hasPendingOrApproved = true;

          for (const run of sorted) {
            const runReports = reportsList.filter((item: any) => {
              const reportObj = item.report || item;
              const rId = reportObj.run_id !== undefined ? reportObj.run_id : reportObj.runId;
              return rId !== undefined && rId !== null && Number(rId) === run.run_id;
            });
            const runHasReport = runReports.some((item: any) => {
              const reportObj = item.report || item;
              const reportStatus = item.report ? reportObj.status : (item.status || reportObj.status);
              return ['pending', 'draft', 'qa_review', 'admin_review', 'rejected', 'approved', 'business_review', 'business_requested_changes', 'changes_added', 'report_updated', 'business_approved'].includes(reportStatus);
            });

            if (!runHasReport) {
              targetRun = run;
              hasPendingOrApproved = false;
              break;
            }
          }

          // Scan all completed runs to see how many have reports ready to download
          let count = 0;
          let singleId = null;
          let singleTitle = "";
          let singleRunId = null;

          for (const run of sorted) {
            const runReports = reportsList.filter((item: any) => {
              const reportObj = item.report || item;
              const rId = reportObj.run_id !== undefined ? reportObj.run_id : reportObj.runId;
              return rId !== undefined && rId !== null && Number(rId) === run.run_id;
            });
            const approvedReport = runReports.find((item: any) => {
              const reportObj = item.report || item;
              const reportStatus = item.report ? reportObj.status : (item.status || reportObj.status);
              return ['approved', 'business_approved', 'business_review', 'business_requested_changes'].includes(reportStatus);
            });
            if (approvedReport) {
              count++;
              const reportObj = approvedReport.report || approvedReport;
              singleId = reportObj.id;
              singleTitle = run.run_title;
              singleRunId = run.run_id;
            }
          }

          setDownloadReportCount(count);
          setLatestRunReportDownloadId(singleId);
          setSingleDownloadRunTitle(singleTitle);
          setSingleDownloadRunId(singleRunId);

          if (targetRun) {
            setTargetRunForReport(targetRun);
            setLatestRunHasReport(false);
          } else {
            setTargetRunForReport(sorted[0]);
            setLatestRunHasReport(true);
          }
        }
      } catch (error) {
        console.error("Error checking latest run automated reports:", error);
      }
    };
    checkLatestRunReport();
    (window as any).refreshLatestRunReport = checkLatestRunReport;
    return () => {
      delete (window as any).refreshLatestRunReport;
    };
  }, [id, runs, program?.scheduling_type, role]);

  const handleRequestPentestReport = async (runId?: number) => {
    if (!id) return;
    setIsRequestingReport(true);
    try {
      const payload = {
        company_name: "Default Company",
        program_ids: [parseInt(id, 10)],
        run_id: runId
      };
      await createAutomatedReport(payload);
      toast.success("Pentest report requested successfully!");
      setIsConfirmModalOpen(false);
      await checkAutomatedReports();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to request report.");
    } finally {
      setIsRequestingReport(false);
    }
  };

  const getReturnUrl = () => {
    if (!role) return "/dashboard/programs";
    const isAdmin = [UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.ADMIN_MANAGER].includes(role);
    if (isAdmin && programOwnerCompanyId) return `/dashboard/companies/${programOwnerCompanyId}#programs`;
    return "/dashboard/programs";
  };

  const docFiles = program?.asset_documentation?.documentation_files;
  const hasDocumentation = !!(
    program?.asset_documentation?.architecture_diagram_url ||
    program?.asset_documentation?.product_documentation_url ||
    program?.asset_documentation?.runbook_url ||
    program?.asset_documentation?.threat_model_url ||
    program?.asset_documentation?.additional_notes ||
    (Array.isArray(docFiles) && docFiles.length > 0) ||
    program?.asset ||
    program?.asset_id
  );

  let availableSections = hasDocumentation
    ? [...sections.slice(0, 2), documentationSection, ...sections.slice(2)]
    : sections;

  // Add Credential Vault for authorized roles
  const canAccessVault = role && ([UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.ADMIN_MANAGER, UserRole.RESEARCHER].includes(role as UserRole));
  if (canAccessVault && program?.id) {
    // Insert after Discovery (index 4 in sections, but index might vary if documentation is present)
    const discoveryIndex = availableSections.findIndex(s => s.name === "Discovery");
    if (discoveryIndex !== -1) {
      availableSections = [
        ...availableSections.slice(0, discoveryIndex + 1),
        credentialVaultSection,
        ...availableSections.slice(discoveryIndex + 1)
      ];
    } else {
      availableSections = [...availableSections, credentialVaultSection];
    }
  }



  useEffect(() => {
    if (urlSection) {
      const sectionIndex = availableSections.findIndex(s => s.name === urlSection);
      if (sectionIndex !== -1) {
        setActiveSection(urlSection);
        setActiveTab(sectionIndex);
      }
    }
  }, [urlSection, availableSections]);

  const isAdminViewGroup = [UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.ADMIN_MANAGER, UserRole.QA].includes(role as UserRole);

  // Hide "Scheduled Runs" section from researchers when there are no runs
  // (e.g., legacy V1 programs that don't use the runs system)
  if (role === UserRole.RESEARCHER && runs.length === 0) {
    availableSections = availableSections.filter(s => s.name !== "Scheduled Runs");
  }
  const handleScheduleRunClick = () => {
    setIsHamburgerOpen(true);
    setActiveSection("Scheduled Runs");
    setTriggerAutoOpen(Date.now());
  };

  const isDraft = isProgramDraft(program);

  const standardNotification = isDraft ? (
    <div className="flex items-center gap-3 bg-amber-50 border border-amber-500 px-4 py-2 rounded-lg shadow-sm">
      <span className="text-sm font-medium text-amber-700">Draft Program: Please complete fields</span>
      <button onClick={() => navigate(`/dashboard/programs/${id}/edit`)} className="text-xs font-bold text-white bg-amber-600 px-3 py-1.5 rounded-md">Edit Program</button>
    </div>
  ) : isScopeSnapshotPending(program) ? (
    <div className="flex items-center gap-3 bg-red-50 border border-red-500 px-4 py-2 rounded-lg shadow-sm">
      <span className="text-sm font-medium text-red-700">Action Required: Verify Snapshot</span>
      <button onClick={handleScheduleRunClick} className="text-xs font-bold text-white bg-red-600 px-4 py-1.5 rounded-md">Review Snapshot</button>
    </div>
  ) : (isAdminViewGroup && isScopePendingVerification(program)) ? (
    <div className="flex items-center gap-3 bg-blue-50 border border-blue-500 px-4 py-2 rounded-lg animate-fade-in shadow-sm">
      <div className="flex items-center gap-2">
        <div className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
        </div>
        <span className="text-sm font-medium text-blue-700">
          Verification Required: Scope Snapshot is ready for review
        </span>
      </div>
      <button
        onClick={handleScheduleRunClick}
        className="text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-md transition-colors"
      >
        Verify Snapshot
      </button>
    </div>
  ) : (program?.scheduling_type === 'continuous' && runs.length === 0) ? (
    <div className="flex items-center gap-3 bg-green-50 border border-green-500 px-4 py-2 rounded-lg animate-fade-in shadow-sm">
      <div className="flex items-center gap-2">
        <div className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
        </div>
        <span className="text-sm font-medium text-green-500">
          Action Required: Schedule your first run
        </span>
      </div>
      <div className="relative">
        <button
          onClick={handleScheduleRunClick}
          className="text-xs font-bold text-white bg-blue-900 hover:bg-blue-700 px-4 py-1.5 rounded-md transition-all animate-pulse ring-4 ring-blue-500/30"
        >
          Schedule Now
        </button>

        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, y: 15, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 15, scale: 0.9 }}
            className="absolute top-full right-0 mt-4 w-80 p-6 bg-gradient-to-br from-[#1a1f37] to-[#0f172a] text-white rounded-[24px] shadow-[0_20px_50px_rgba(0,0,0,0.4)] z-50 border border-white/10"
          >
            <div className="flex items-center gap-3 mb-2.5">
              <div className="w-2.5 h-2.5 rounded-full bg-green-500 shadow-[0_0_12px_rgba(34,197,94,0.6)] animate-pulse" />
              <span className="text-[10px] font-black text-green-400 uppercase tracking-[0.2em]">Guidance</span>
            </div>
            <h4 className="text-[15px] font-black mb-1.5 tracking-tight">Schedule your first run!</h4>
            <p className="text-[12px] text-gray-400 font-medium leading-relaxed">
              Your continuous program is ready. Every continuous program needs at least one <b>Scheduled Run</b> to become active and testable.
            </p>

            {/* Arrow pointing up to button */}
            <div className="absolute -top-2 right-[40px] w-4 h-4 bg-[#1a1f37] rotate-45 border-l border-t border-white/10" />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  ) : null;

  const feedbackNotifications = pendingFeedbacksForThisProgram
    .filter((pf) => pf.run_id == null)
    .map((pf) => (
      <div key={`fb-${pf.feedback_id}`} className="flex items-center gap-3 bg-blue-50 border border-blue-500 px-4 py-2 rounded-lg animate-fade-in shadow-sm">
        <div className="flex items-center gap-2">
          <FiMessageSquare className="text-blue-600" />
          <span className="text-sm font-medium text-blue-700">
            Action Required: Feedback Requested for the Overall Program
          </span>
        </div>
        <button
          onClick={() => navigate(`/dashboard/programs/${program!.id}/feedback`)}
          className="text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 px-4 py-1.5 rounded-md transition-colors"
        >
          Give Feedback
        </button>
      </div>
    ));

  const completedRuns = [...(runs || [])].filter(r => r.status === 'completed');
  const sortedRuns = completedRuns.sort((a, b) => b.run_id - a.run_id);
  const latestRun = targetRunForReport || sortedRuns[0];
  const showGenerateReportBtn = isAuthorizedForReport && program?.scheduling_type === 'continuous' && latestRun && !latestRunHasReport;

  const notificationContent = (standardNotification || feedbackNotifications.length > 0) ? (
    <div className="flex flex-col gap-2 items-end">
      {standardNotification}
      {feedbackNotifications}
    </div>
  ) : null;

  return (
    <section className="flex min-h-full flex-col bg-ctb-grey-250">
      <DetailViewLayout
        link={getReturnUrl()}
        label="Return to Programs"
        className="grid grid-cols-1"
        containerClassName="flex-grow"
        headerAction={notificationContent}
      >
        {isLoading ? (
          <p>Loading...</p>
        ) : program ? (
          <section className="pb-10 -mt-3">
            <div className="flex">
              {role && (
                <Sidebar
                  activeSection={activeSection}
                  allSections={availableSections}
                  setActiveSection={(section) => {
                    setActiveSection(section);
                    // Clear the query params when changing sections manually to prevent URL lock-in
                    navigate(location.pathname, { replace: true });
                  }}
                  role={role}
                  onToggle={setIsHamburgerOpen}
                  isOpen={isHamburgerOpen}
                />
              )}

              <div className="flex-[10]">
                <Tab tabLabel="Program Information" Icon={HiInformationCircle}>
                  <ProgramHighlight isHamburgerOpen={isHamburgerOpen} />

                  {isAdminViewGroup && (
                    <div className="mt-4">
                      {activeSection === "Overview" && (
                        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-gray-100">
                            <div className="flex items-center gap-3">
                              <h3 className="text-base font-bold text-gray-900">Current Phase</h3>
                              <PhaseStatusPill status={toPhaseStatus(program?.phase_status)} />
                              
                              {/* Mark Ready — pretesting only */}
                              {(() => {
                                const allowed = [UserRole.RESEARCHER, UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.QA, UserRole.ADMIN_MANAGER];
                                const canMarkReady = role !== undefined && allowed.includes(role as UserRole) && toPhaseStatus(program?.phase_status) === PhaseStatus.PRETESTING;
                                return canMarkReady ? (
                                  <MarkReadyButton
                                    programId={program!.id}
                                    currentPhase={toPhaseStatus(program?.phase_status)}
                                    onSuccess={() => window.location.reload()}
                                    className="py-1.5 px-3.5 text-xs font-semibold"
                                  />
                                ) : null;
                              })()}
                            </div>
                          </div>

                          {/* Info Alert Box */}
                          {isAuthorizedForReport && program?.scheduling_type !== 'continuous' && hasPendingReport && latestReport?.status !== 'business_review' && latestReport?.status !== 'business_approved' && (
                            <div className="mt-4 flex items-start sm:items-center gap-3 bg-blue-50 border border-blue-200 px-4 py-3 rounded-lg text-xs text-blue-700 animate-fade-in">
                              <HiInformationCircle className="w-4 h-4 shrink-0 text-blue-500 mt-0.5 sm:mt-0" />
                              <span>
                                <strong className="font-semibold text-blue-900">Report delivery takes 24-48 hours : </strong>
                                Our security team conducts quality assurance and prepares the PDF report before it is ready for download.
                              </span>
                            </div>
                          )}

                          <div className="mt-6">
                            <PhaseTimeline currentPhase={toPhaseStatus(program?.phase_status)} expectedEndDate={program?.expected_end_date as any} />
                          </div>

                          {/* Quick Actions Section */}
                          <div className="mt-8 pt-6 border-t border-gray-100">
                            <h4 className="text-sm font-bold text-gray-900 tracking-wide uppercase mb-4">Quick Actions</h4>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                              
                              {/* Card 1: Download / Request Report */}
                              {isAuthorizedForReport && program?.scheduling_type !== 'continuous' && (() => {
                                const isRecommended = hasApprovedReport && !hasPendingReport;
                                return (
                                  <div className={`flex flex-col p-5 rounded-xl border transition-all duration-200 ${
                                    isRecommended 
                                      ? 'border-green-300 bg-green-50/10 shadow-sm' 
                                      : 'border-gray-200 bg-white hover:border-gray-300'
                                  }`}>
                                    <div className="flex items-start justify-between mb-3">
                                      <div className="p-2.5 bg-[#010D2C] text-white rounded-lg">
                                        <FiDownload className="w-4 h-4" />
                                      </div>
                                      {isRecommended && (
                                        <span className="bg-blue-100 text-blue-800 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                                          Recommended
                                        </span>
                                      )}
                                    </div>
                                    <h5 className="text-sm font-bold text-gray-900">
                                      {hasPendingReport ? 'Report in QA' : hasApprovedReport ? 'Download report' : 'Request report'}
                                    </h5>
                                    <p className="text-xs text-gray-500 mt-1 mb-4 flex-grow leading-relaxed">
                                      {hasPendingReport 
                                        ? 'Available once the current version clears QA review.' 
                                        : hasApprovedReport 
                                          ? `Get the final PDF for ${program?.title || 'this program'}` 
                                          : 'Request the final pentest report for this program.'}
                                    </p>

                                    {/* Action button/link */}
                                    {hasPendingReport && (
                                      <span className="text-xs font-semibold text-gray-400 mt-auto flex items-center gap-1">
                                        {approvedReports.length > 0 ? 'New Version — In QA' : 'Report — In QA'}
                                      </span>
                                    )}

                                    {hasApprovedReport && !hasPendingReport && (() => {
                                      if (approvedReports.length > 1) {
                                        return (
                                          <div className="relative mt-auto">
                                            <select
                                              onChange={(e) => {
                                                if (e.target.value) {
                                                  navigate(`/dashboard/pentest-reports/${e.target.value}/edit`);
                                                }
                                              }}
                                              className="text-xs font-semibold text-green-600 hover:text-green-700 bg-transparent border-none p-0 pr-6 outline-none cursor-pointer w-full focus:ring-0"
                                              defaultValue=""
                                            >
                                              <option value="" disabled className="text-gray-500 font-medium">Download Version... ➔</option>
                                              {approvedReports.map((item, idx) => {
                                                const reportObj = item.report || item;
                                                const versionNum = (item as any).version_number || (reportObj as any).version_number || (idx + 1);
                                                return (
                                                  <option key={reportObj.id} value={reportObj.id} className="text-gray-700 bg-white">
                                                    {`Version ${versionNum}`}
                                                  </option>
                                                );
                                              })}
                                            </select>
                                          </div>
                                        );
                                      }
                                      return approvedReportId ? (
                                        <button
                                          onClick={() => navigate(`/dashboard/pentest-reports/${approvedReportId}/edit`)}
                                          className="text-xs font-semibold text-green-600 hover:text-green-700 mt-auto flex items-center gap-1 self-start group transition-all"
                                        >
                                          Download <span className="group-hover:translate-x-1 transition-transform inline-block">➔</span>
                                        </button>
                                      ) : null;
                                    })()}

                                    {!hasApprovedReport && !hasPendingReport && (
                                      <button
                                        onClick={() => setIsConfirmModalOpen(true)}
                                        disabled={isRequestingReport || isCheckingReport || isBeforeDate(program?.expected_end_date || '')}
                                        className={`text-xs font-semibold mt-auto flex items-center gap-1 self-start ${
                                          isRequestingReport || isCheckingReport || isBeforeDate(program?.expected_end_date || '')
                                            ? 'text-gray-400 cursor-not-allowed'
                                            : 'text-green-600 hover:text-green-700'
                                        }`}
                                      >
                                        {isRequestingReport ? 'Requesting...' : isCheckingReport ? 'Checking...' : 'Request ➔'}
                                      </button>
                                    )}
                                  </div>
                                );
                              })()}

                              {/* Card 2: Request Feedback */}
                              {role && [UserRole.ADMIN, UserRole.SUB_ADMIN].includes(role as UserRole) && (
                                <div className="flex flex-col p-5 rounded-xl border border-gray-200 bg-white hover:border-gray-300 transition-all duration-200">
                                  <div className="flex items-start justify-between mb-3">
                                    <div className="p-2.5 bg-[#010D2C] text-white rounded-lg">
                                      <FiMessageSquare className="w-4 h-4" />
                                    </div>
                                  </div>
                                  <h5 className="text-sm font-bold text-gray-900">Request feedback</h5>
                                  <p className="text-xs text-gray-500 mt-1 mb-4 flex-grow leading-relaxed">
                                    Ask the client to review this program
                                  </p>

                                  <button
                                    onClick={handleAdminRequestFeedbackClick}
                                    disabled={isRequestingFeedback || isUndoingFeedback || feedbackStatus?.has_submitted}
                                    className={`text-xs font-semibold mt-auto flex items-center gap-1 self-start ${
                                      isRequestingFeedback || isUndoingFeedback || feedbackStatus?.has_submitted
                                        ? 'text-gray-400 cursor-not-allowed'
                                        : feedbackStatus?.is_requested
                                          ? 'text-red-600 hover:text-red-700'
                                          : 'text-green-600 hover:text-green-700'
                                    }`}
                                  >
                                    {isRequestingFeedback ? 'Requesting...' :
                                      isUndoingFeedback ? 'Taking back...' :
                                        feedbackStatus?.has_submitted ? 'Feedback Received' :
                                          feedbackStatus?.is_requested ? 'Take Back Request ➔' :
                                            'Continue ➔'}
                                  </button>
                                </div>
                              )}

                              {/* Card 3: Send Summary Email */}
                              {role && [UserRole.ADMIN, UserRole.SUB_ADMIN].includes(role as UserRole) && (() => {
                                const isProgramEnded = program?.expected_end_date
                                  ? new Date(program.expected_end_date) <= new Date()
                                  : false;
                                const isClosedOrCompleted = isProgramEnded || ['completed', 'closed'].includes(program?.phase_status?.toLowerCase() || '') || ['completed', 'closed'].includes((program as any)?.status?.toLowerCase() || '');
                                const isContinuous = (program as any)?.scheduling_type === 'continuous';
                                const completedRuns = runs?.filter(run => run.status === 'completed') || [];
                                const hasCompletedRuns = completedRuns.length > 0;
                                const shouldShow = isClosedOrCompleted || (isContinuous && hasCompletedRuns);
                                
                                return (
                                  <div className="flex flex-col p-5 rounded-xl border border-gray-200 bg-white hover:border-gray-300 transition-all duration-200">
                                    <div className="flex items-start justify-between mb-3">
                                      <div className="p-2.5 bg-[#010D2C] text-white rounded-lg">
                                        <HiMail className="w-4 h-4" />
                                      </div>
                                    </div>
                                    <h5 className="text-sm font-bold text-gray-900">Send summary email</h5>
                                    <p className="text-xs text-gray-500 mt-1 mb-4 flex-grow leading-relaxed">
                                      Email the program summary report to stakeholders
                                    </p>

                                    {!shouldShow ? (
                                      <span className="text-xs font-semibold text-gray-400 mt-auto">
                                        Unavailable
                                      </span>
                                    ) : !isContinuous ? (
                                      <button
                                        onClick={() => {
                                          setSelectedRunForSummary(null);
                                          setIsSummaryModalOpen(true);
                                        }}
                                        className="text-xs font-semibold text-green-600 hover:text-green-700 mt-auto flex items-center gap-1 self-start group transition-all"
                                      >
                                        Continue <span className="group-hover:translate-x-1 transition-transform inline-block">➔</span>
                                      </button>
                                    ) : (
                                      <div className="relative mt-auto">
                                        <select
                                          onChange={(e) => {
                                            if (e.target.value) {
                                              const run = completedRuns.find(r => String(r.run_id) === e.target.value);
                                              if (run) {
                                                setSelectedRunForSummary(run);
                                                setIsSummaryModalOpen(true);
                                              }
                                              e.target.value = "";
                                            }
                                          }}
                                          className="text-xs font-semibold text-green-600 hover:text-green-700 bg-transparent border-none p-0 pr-6 outline-none cursor-pointer w-full focus:ring-0"
                                          defaultValue=""
                                        >
                                          <option value="" disabled className="text-gray-500 font-medium">Send Summary... ➔</option>
                                          {completedRuns.map(run => (
                                            <option key={run.run_id} value={run.run_id} className="text-gray-700 bg-white">
                                              {`${run.run_title} (${run.status})`}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}

                              {/* Card 4: Start New Version */}
                              {isAuthorizedForReport && program?.scheduling_type !== 'continuous' && (
                                <div className="flex flex-col p-5 rounded-xl border border-gray-200 bg-white hover:border-gray-300 transition-all duration-200">
                                  <div className="flex items-start justify-between mb-3">
                                    <div className="p-2.5 bg-[#010D2C] text-white rounded-lg">
                                      <HiLockClosed className="w-4 h-4" />
                                    </div>
                                    {hasPendingReport && (
                                      <span className="bg-green-100 text-green-800 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                                        In QA
                                      </span>
                                    )}
                                  </div>
                                  <h5 className="text-sm font-bold text-gray-900">Start new version</h5>
                                  <p className="text-xs text-gray-500 mt-1 mb-4 flex-grow leading-relaxed">
                                    {hasPendingReport 
                                      ? 'Available once the current version clears QA review.' 
                                      : 'Request a new version of the pentest report.'}
                                  </p>

                                  {hasApprovedReport && !hasPendingReport && role && [UserRole.ADMIN, UserRole.SUB_ADMIN].includes(role as UserRole) ? (
                                    <button
                                      onClick={() => setIsConfirmModalOpen(true)}
                                      disabled={isRequestingReport || !latestReport || !['business_review', 'business_approved'].includes(latestReport.status)}
                                      className={`text-xs font-semibold mt-auto flex items-center gap-1 self-start ${
                                        isRequestingReport || !latestReport || !['business_review', 'business_approved'].includes(latestReport.status)
                                          ? 'text-gray-400 cursor-not-allowed'
                                          : 'text-green-600 hover:text-green-700'
                                      }`}
                                      title={
                                        latestReport && !['business_review', 'business_approved'].includes(latestReport.status)
                                          ? `Cannot request new version: previous version is in ${latestReport.status} status`
                                          : 'Request a new version of the report'
                                      }
                                    >
                                      {isRequestingReport ? 'Requesting...' : 'Request ➔'}
                                    </button>
                                  ) : (
                                    <span className="text-xs font-semibold text-gray-400 mt-auto">
                                      Unavailable
                                    </span>
                                  )}
                                </div>
                              )}

                            </div>
                          </div>
                        </div>
                      )}


                      <div className="mt-4">
                        {activeSection === "Overview" ? (
                          <>
                            {showGenerateReportBtn && (
                              <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shadow-sm">
                                <div className="flex items-start gap-3">
                                  <div className="p-2 bg-emerald-100 rounded-lg text-emerald-850 shrink-0">
                                    <HiCheckCircle className="w-5 h-5 text-emerald-600" />
                                  </div>
                                  <div>
                                    <h4 className="text-sm font-semibold text-emerald-900">Scheduled Run Completed</h4>
                                    <p className="text-xs text-emerald-700 mt-0.5">
                                      The scheduled run <strong className="text-emerald-900">"{latestRun.run_title}"</strong> has completed successfully. You can now generate the pentest report for this run.
                                    </p>
                                  </div>
                                </div>
                                <button
                                  onClick={() => {
                                    const index = availableSections.findIndex(s => s.name === "Scheduled Runs");
                                    if (index !== -1) {
                                      setActiveSection("Scheduled Runs");
                                      setActiveTab(index);
                                      navigate(`${location.pathname}?runId=${latestRun.run_id}&section=Scheduled Runs`, { replace: true });
                                    }
                                  }}
                                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#010D2C] hover:bg-opacity-90 text-white font-medium rounded-lg text-xs transition-all shadow-sm shrink-0 self-start sm:self-center"
                                >
                                  <HiShieldCheck className="w-4 h-4 text-emerald-400" />
                                  Request Pentest Report
                                </button>
                              </div>
                            )}
                            <FullProgramDetails program={program} />
                          </>
                        ) : activeSection === "Attachments" ? (
                          <Attachments program={program} />
                        ) : activeSection === "Scope" ? (
                          <ScopeTable />
                        ) : activeSection === "Test Checklists" ? (
                          <ProgramChecklistsView
                            programId={program!.id}
                            runId={selectedRunId}
                            userRole={role || 0}
                            asset={program.asset}
                          />
                        ) : activeSection === "Reports" ? (
                          <ReportTable />
                        ) : activeSection === "Recent Activity" ? (
                          <RecentActivity />
                        ) : activeSection === "Management" ? (
                          <Management />
                        ) : activeSection === "Scheduled Runs" ? (
                          <ScheduledRuns triggerAutoOpen={triggerAutoOpen} />
                        ) : activeSection === "Documentation" ? (
                          <Documentation
                            assetDocumentation={program?.asset_documentation}
                            asset={program?.asset}
                          />
                        ) : activeSection === "Discovery" ? (
                          <DiscoveredEndpointsTable
                            role={role as UserRole}
                            assetId={program?.asset_id}
                          />
                        ) : activeSection === "Credential Vault" ? (
                          <div className="space-y-6">
                            <div className="bg-white rounded-lg shadow border p-6">
                              <CredentialVault programId={program.id} />
                            </div>
                          </div>
                        ) : activeSection === "Terms & Conditions" ? (
                          <TermsConditions />
                        ) : null}
                      </div>
                    </div>
                  )}
                </Tab>
              </div>

              {(role === UserRole.ADMIN || role === UserRole.QA || role === UserRole.ADMIN_MANAGER || role === UserRole.SUB_ADMIN) && <RightbarProgram />}
            </div>

            {!isAdminViewGroup && (
              <div className={`${isHamburgerOpen ? "-ml-2" : "ml-11"} mt-4`}>
                {activeSection === "Overview" && (
                  <div className="bg-white rounded-lg shadow p-4 border">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <h3 className="text-sm font-medium text-gray-700">Current Phase</h3>
                        <PhaseStatusPill status={toPhaseStatus(program?.phase_status)} />
                      </div>
                      <div className="flex items-center gap-3">
                        {feedbackStatus?.has_submitted && feedbackStatus.feedback?.overall_program_rating != null && (
                          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 border border-amber-200 rounded-lg text-xs font-bold text-amber-800 shadow-sm" title="Program Overall Rating">
                            <FiStar className="w-4 h-4 fill-amber-400 text-amber-500" />
                            <span>Program Rating: {feedbackStatus.feedback.overall_program_rating.toFixed(1)} / 5.0</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-4">
                      <PhaseTimeline currentPhase={toPhaseStatus(program?.phase_status)} expectedEndDate={program?.expected_end_date as any} />
                    </div>

                    {/* Info Alert Box */}
                    {isAuthorizedForReport && program?.scheduling_type !== 'continuous' && hasPendingReport && latestReport?.status !== 'business_review' && latestReport?.status !== 'business_approved' && (
                      <div className="mt-4 flex items-start sm:items-center gap-3 bg-blue-50 border border-blue-200 px-4 py-3 rounded-lg text-xs text-blue-700 animate-fade-in">
                        <HiInformationCircle className="w-4 h-4 shrink-0 text-blue-500 mt-0.5 sm:mt-0" />
                        <span>
                          <strong className="font-semibold text-blue-900">Report delivery takes 24-48 hours : </strong>
                          Our security team conducts quality assurance and prepares the PDF report before it is ready for download.
                        </span>
                      </div>
                    )}

                    <div className="mt-4 flex items-center gap-3 justify-end">
                      {(() => {
                        const allowed = [UserRole.RESEARCHER, UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.QA, UserRole.ADMIN_MANAGER];
                        const canMarkReady = role !== undefined && allowed.includes(role as UserRole) && toPhaseStatus(program?.phase_status) === PhaseStatus.PRETESTING;
                        return canMarkReady ? (
                          <MarkReadyButton
                            programId={program!.id}
                            currentPhase={toPhaseStatus(program?.phase_status)}
                            onSuccess={() => window.location.reload()}
                          />
                        ) : null;
                      })()}
                      {role === UserRole.RESEARCHER && (
                        <button
                          className="inline-flex items-center gap-2 rounded-lg px-5 py-3 font-normal text-sm text-[#58CC02] bg-[#010D2C] hover:opacity-90"
                          onClick={() => program && navigate(`/dashboard/reports/new/${program.id}`)}
                        >
                          <img src={reportBugIcon} alt="" className="w-4 h-4" />
                          Report a Bug
                        </button>
                      )}
                      {(role === UserRole.BUSINESS || role === UserRole.BUSINESS_ADMINISTRATOR) && (feedbackStatus?.is_requested || feedbackStatus?.has_submitted) && (
                        <button
                          onClick={() => feedbackStatus?.is_requested && navigate(`/dashboard/programs/${program!.id}/feedback`)}
                          disabled={feedbackStatus?.has_submitted}
                          className={`inline-flex items-center gap-2 rounded-lg px-5 py-3 text-sm font-normal transition-colors ${feedbackStatus?.has_submitted ? 'bg-gray-300 text-gray-500' : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                        >
                          <FiMessageSquare className="w-4 h-4" />
                          {feedbackStatus?.has_submitted ? 'Feedback Submitted' : 'Give Feedback'}
                        </button>
                      )}

                      {isAuthorizedForReport && program?.scheduling_type !== 'continuous' && (
                        <div className="flex flex-col sm:flex-row items-center gap-3">
                          {/* Show In QA badge whenever there is a pending version */}
                          {hasPendingReport && (
                            <button
                              disabled
                              className="inline-flex items-center gap-2 rounded-lg px-5 py-3 text-sm font-normal text-white bg-gray-400 cursor-not-allowed transition-all duration-200"
                            >
                              {approvedReports.length > 0 ? 'New Version — In QA' : 'Report — In QA'}
                            </button>
                          )}
                          {/* Always show download for approved versions, even when pending exists */}
                          {hasApprovedReport ? (
                            <div className="flex items-center gap-2">
                              {!hasPendingReport && (
                                <button
                                  disabled
                                  className="inline-flex items-center gap-2 rounded-lg px-5 py-3 text-sm font-normal text-white bg-gray-400 cursor-not-allowed transition-all duration-200"
                                >
                                  Report Generated — Available to Download
                                </button>
                              )}
                              {approvedReports.length > 1 ? (
                                <select
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      navigate(`/dashboard/pentest-reports/${e.target.value}/edit`);
                                    }
                                  }}
                                  className="inline-flex items-center gap-2 rounded-lg px-5 py-3 text-sm font-medium text-white bg-green-600 hover:bg-green-700 transition-all duration-200 outline-none cursor-pointer"
                                  defaultValue=""
                                >
                                  <option value="" disabled className="text-gray-700 bg-white font-medium">Download Version...</option>
                                  {approvedReports.map((item, idx) => {
                                    const reportObj = item.report || item;
                                    const versionNum = (item as any).version_number || (reportObj as any).version_number || (idx + 1);
                                    return (
                                      <option key={reportObj.id} value={reportObj.id} className="text-gray-700 bg-white">
                                        {`Version ${versionNum}`}
                                      </option>
                                    );
                                  })}
                                </select>
                              ) : (
                                approvedReportId && (
                                  <button
                                    onClick={() => navigate(`/dashboard/pentest-reports/${approvedReportId}/edit`)}
                                    className="inline-flex items-center gap-2 rounded-lg px-5 py-3 text-sm font-medium text-white bg-green-600 hover:bg-green-700 transition-all duration-200"
                                    title="Download PDF Report"
                                  >
                                    <FiDownload className="w-4 h-4" />
                                    Download
                                  </button>
                                )
                              )}
                              {role && [UserRole.ADMIN, UserRole.SUB_ADMIN].includes(role as UserRole) && !hasPendingReport && (
                                <button
                                  onClick={() => setIsConfirmModalOpen(true)}
                                  disabled={isRequestingReport || !latestReport || !['business_review', 'business_approved'].includes(latestReport.status)}
                                  className={`inline-flex items-center gap-2 rounded-lg px-5 py-3 text-sm font-normal text-white transition-all duration-200 ${isRequestingReport || !latestReport || !['business_review', 'business_approved'].includes(latestReport.status)
                                      ? 'bg-gray-400 cursor-not-allowed'
                                      : 'bg-[#010D2C] hover:opacity-90'
                                    }`}
                                  title={
                                    latestReport && !['business_review', 'business_approved'].includes(latestReport.status)
                                      ? `Cannot request new version: previous version is in ${latestReport.status} status`
                                      : 'Request a new version of the report'
                                  }
                                >
                                  {isRequestingReport ? 'Requesting...' : 'Request New Version'}
                                </button>
                              )}
                            </div>
                          ) : !hasPendingReport && (
                            <button
                              onClick={() => setIsConfirmModalOpen(true)}
                              disabled={isRequestingReport || isCheckingReport || isBeforeDate(program?.expected_end_date || '')}
                              className={`inline-flex items-center gap-2 rounded-lg px-5 py-3 text-sm font-normal text-white transition-all duration-200 ${isRequestingReport || isCheckingReport || isBeforeDate(program?.expected_end_date || '')
                                  ? 'bg-gray-400 cursor-not-allowed'
                                  : 'bg-[#010D2C] hover:opacity-90'
                                }`}
                            >
                              {isRequestingReport ? 'Requesting...' : isCheckingReport ? 'Checking...' : 'Request Pentest Report'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}


                <div className="mt-4">
                  {activeSection === "Overview" ? (
                    <>
                      {(showGenerateReportBtn || (isAuthorizedForReport && program?.scheduling_type === 'continuous' && downloadReportCount > 0)) && (
                        <div className="mb-6 bg-emerald-50 border border-emerald-255 rounded-xl p-4 divide-y divide-emerald-200/60 space-y-3.5 shadow-sm">
                          {showGenerateReportBtn && (
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3.5 last:pb-0">
                              <div className="flex items-start gap-3">
                                <div className="p-1.5 bg-emerald-100 rounded-lg text-emerald-805 shrink-0 mt-0.5">
                                  <HiCheckCircle className="w-5 h-5 text-emerald-600" />
                                </div>
                                <div>
                                  <h4 className="text-sm font-semibold text-emerald-900">Scheduled Run Completed</h4>
                                  <p className="text-xs text-emerald-700 mt-0.5">
                                    The scheduled run <strong className="text-emerald-900">"{latestRun.run_title}"</strong> has completed. You can now request the pentest report.
                                  </p>
                                </div>
                              </div>
                              <button
                                onClick={() => {
                                  const index = availableSections.findIndex(s => s.name === "Scheduled Runs");
                                  if (index !== -1) {
                                    setActiveSection("Scheduled Runs");
                                    setActiveTab(index);
                                    navigate(`${location.pathname}?runId=${latestRun.run_id}&section=Scheduled Runs`, { replace: true });
                                  }
                                }}
                                className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#010D2C] hover:bg-opacity-90 text-white font-medium rounded-lg text-xs transition-all shadow-sm shrink-0 self-start sm:self-center"
                              >
                                <HiShieldCheck className="w-4 h-4 text-emerald-400" />
                                Request Pentest Report
                              </button>
                            </div>
                          )}
                          {isAuthorizedForReport && program?.scheduling_type === 'continuous' && downloadReportCount > 0 && (
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-3.5 first:pt-0">
                              {downloadReportCount === 1 ? (
                                <>
                                  <div className="flex items-start gap-3">
                                    <div className="p-1.5 bg-emerald-100 rounded-lg text-emerald-850 shrink-0 mt-0.5">
                                      <HiCheckCircle className="w-5 h-5 text-emerald-600" />
                                    </div>
                                    <div>
                                      <h4 className="text-sm font-semibold text-emerald-900">Pentest Report Available to Download</h4>
                                      <p className="text-xs text-emerald-700 mt-0.5">
                                        The report for <strong className="text-emerald-900">"{singleDownloadRunTitle}"</strong> is generated and ready.
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3 shrink-0 self-start sm:self-center">
                                    <button
                                      onClick={() => {
                                        const index = availableSections.findIndex(s => s.name === "Scheduled Runs");
                                        if (index !== -1) {
                                          setActiveSection("Scheduled Runs");
                                          setActiveTab(index);
                                          navigate(`${location.pathname}?runId=${singleDownloadRunId}&section=Scheduled Runs`, { replace: true });
                                        }
                                      }}
                                      className="text-xs font-semibold text-emerald-850 hover:text-emerald-950 transition-colors px-2 py-1"
                                    >
                                      View in Runs
                                    </button>
                                    <button
                                      onClick={() => navigate(`/dashboard/pentest-reports/${latestRunReportDownloadId}/edit`)}
                                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg text-xs transition-all shadow-sm"
                                    >
                                      <FiDownload className="w-4 h-4" />
                                      Download Pentest Report
                                    </button>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className="flex items-start gap-3">
                                    <div className="p-1.5 bg-emerald-100 rounded-lg text-emerald-850 shrink-0 mt-0.5">
                                      <HiCheckCircle className="w-5 h-5 text-emerald-600" />
                                    </div>
                                    <div>
                                      <h4 className="text-sm font-semibold text-emerald-900">Pentest Reports Available to Download</h4>
                                      <p className="text-xs text-emerald-700 mt-0.5">
                                        You have <strong className="text-emerald-900">{downloadReportCount}</strong> pentest reports generated and ready.
                                      </p>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => {
                                      const index = availableSections.findIndex(s => s.name === "Scheduled Runs");
                                      if (index !== -1) {
                                        setActiveSection("Scheduled Runs");
                                        setActiveTab(index);
                                        navigate(`${location.pathname}?section=Scheduled Runs`, { replace: true });
                                      }
                                    }}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg text-xs transition-all shadow-sm shrink-0 self-start sm:self-center"
                                  >
                                    <HiShieldCheck className="w-4 h-4 text-emerald-100" />
                                    Go to Scheduled Runs
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      <FullProgramDetails program={program} />
                    </>
                  ) : activeSection === "Attachments" ? (
                    <Attachments program={program} />
                  ) : activeSection === "Scope" ? (
                    <ScopeTable />
                  ) : activeSection === "Reports" ? (
                    <ReportTable />
                  ) : activeSection === "Test Checklists" ? (
                    <ProgramChecklistsView
                      programId={program!.id}
                      runId={selectedRunId}
                      userRole={role || 0}
                      asset={program.asset}
                    />
                  ) : activeSection === "Recent Activity" ? (
                    <RecentActivity />
                  ) : activeSection === "Documentation" ? (
                    <Documentation assetDocumentation={program?.asset_documentation} asset={program?.asset} />
                  ) : activeSection === "Scheduled Runs" ? (
                    <ScheduledRuns triggerAutoOpen={triggerAutoOpen} />
                  ) : activeSection === "Discovery" ? (
                    <DiscoveredEndpointsTable
                      role={role as UserRole}
                      assetId={program?.asset_id}
                    />
                  ) : activeSection === "Credential Vault" ? (
                    <div className="space-y-6">
                      <div className="bg-white rounded-lg shadow border p-6">
                        <CredentialVault programId={program.id} />
                      </div>
                    </div>
                  ) : activeSection === "Terms & Conditions" ? (
                    <TermsConditions />
                  ) : null}
                </div>
              </div>
            )}
          </section>
        ) : (
          <ErrorMessage message="Program not found" clearError={() => { }} />
        )}
      </DetailViewLayout>

      <PentestReportConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleRequestPentestReport}
        programId={id ? parseInt(id, 10) : 0}
      />
      {isFeedbackModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-start justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Request Feedback</h3>
              <button
                onClick={() => setIsFeedbackModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>
            {program?.scheduling_type === 'continuous' ? (
              <>
                <p className="mb-4 text-sm text-gray-600">
                  For continuous programs, you can request feedback for the overall program or a specific run.
                </p>
                <div className="mb-6">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Target Feedback Scope
                  </label>
                  <select
                    value={feedbackSelectedRunId.toString()}
                    onChange={(e) => setFeedbackSelectedRunId(e.target.value === 'overall' ? 'overall' : parseInt(e.target.value, 10))}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="overall">Overall Program</option>
                    {runs.map((run: any) => (
                      <option key={run.run_id} value={run.run_id}>
                        {run.run_title || `Run #${run.run_id}`} ({new Date(run.start_date || run.createdAt).toLocaleDateString()})
                      </option>
                    ))}
                  </select>
                </div>
              </>
            ) : (
              <p className="mb-6 text-sm text-gray-600">
                Are you sure you want to request feedback from the business owner for this program? This will send them a notification.
              </p>
            )}
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setIsFeedbackModalOpen(false)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => executeAdminRequestFeedback(feedbackSelectedRunId === 'overall' ? undefined : feedbackSelectedRunId)}
                disabled={isRequestingFeedback}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isRequestingFeedback ? 'Requesting...' : 'Request Feedback'}
              </button>
            </div>
          </div>
        </div>
      )}
      {isUndoConfirmModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-start justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Take Back Request</h3>
              <button
                onClick={() => setIsUndoConfirmModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>
            <p className="mb-6 text-sm text-gray-600">
              Are you sure you want to undo the feedback request? The business owner will no longer see the pending request.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setIsUndoConfirmModalOpen(false)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={executeAdminUndoFeedback}
                disabled={isUndoingFeedback}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isUndoingFeedback ? 'Taking back...' : 'Take Back Request'}
              </button>
            </div>
          </div>
        </div>
      )}
      {program && (
        <SendSummaryEmailModal
          isOpen={isSummaryModalOpen}
          onClose={() => setIsSummaryModalOpen(false)}
          programId={program.id}
          programTitle={program.title}
          runId={selectedRunForSummary?.run_id}
          runTitle={selectedRunForSummary?.run_title}
        />
      )}
      <Footer />
    </section>
  );
};

export default ProgramPage;
